import React from "react";
import SharedLayout from "./SharedLayout";
import "../styles/shared.css";

const PCPView = ({ user, onLoginClick }) => {
  return (
    <SharedLayout user={user} onLoginClick={onLoginClick}>
      <div className="content-container">
        <h1>A Valuable Tool for Primary Care Physicians and EMRs ("PCPs")</h1>

        <section className="content-section">
          <p>
            <span className="sigmund-text">Sigmund</span> was not designed to
            replace clinical judgment, rather an adjunctive decision support
            tool. By providing evidence-based, data-driven insights,{" "}
            <span className="sigmund-text">Sigmund</span> allows PCPs to make
            better-informed decisions about psychiatric referrals* while
            reducing the potential for mis/overdiagnosis and misprescription.
            This can lead to better patient outcomes and more efficient use of
            healthcare resources.
          </p>

          <div className="video-container">
            <div className="video-link">
              <a
                href="https://www.youtube.com/watch?v=jG2DO8GJRaI&t=135s"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  marginTop: "10px",
                  color: "#1976d2",
                  textDecoration: "underline",
                  fontSize: "1.1rem",
                }}
              >
                Watch the Primary Care Physician Interview on YouTube
              </a>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>
            Key Features of <span className="sigmund-text">Sigmund</span>{" "}
            Technology:
          </h2>
          <ul>
            {[
              {
                title: "Clinically-studied",
                description:
                  "A clinical study with York University considered DNA-based predispositions to depression and anxiety and found that Sigmund's algorithms generated very high accuracies across multiple metrics. Sigmund is validated.",
              },
              {
                title: "Comprehensive Data Integration",
                description:
                  "Sigmund incorporates a wide array of environmental (life experience) data points in its Reference Database to ensure that mental health assessments are evidence-based, holistic and individualized. Sigmund recognizes that you are not the same as everyone else. Sigmund is well-informed.",
              },
              {
                title: "Expert guidance",
                description:
                  "Sigmund's assessment algorithms are guided by actual diagnoses from numerous psychiatric experts. Sigmund mitigates individual bias.",
              },
              {
                title: 'Electroencephalogram ("EEG") Calibration',
                description:
                  'Using real-time, controlled ("EEG") data from Reference Database participants, Sigmund established a "population normal" baseline to which individual data records were compared. Divergence-from-baseline patterns were correlated to environmental answer patterns to calibrate precision. This approach allows for the benefit of EEG calibration without new users having to provide EEG data. Sigmund simplifies.',
              },
              {
                title: "Genetic predispositions",
                description:
                  'Sigmund\'s custom DNA panel involves numerous anxiety/depression markers to calculate associated anxiety and depression predispositions. DNA structure and implied predispositions are unique to a person. Sigmund considers your unique "foundation".',
              },
              {
                title: "AI/Machine-learning-driven Insights",
                description:
                  "Sigmund uses sophisticated algorithms to analyze complex data relationships and identify patterns that may not be obvious to a human analyst. This leads to more accurate risk assessments and improves the potential for proper pre-emptive intervention. Sigmund is a human/machine hybrid using the best of both worlds.",
              },
            ].map((feature, index) => (
              <li key={index}>
                <h3>{feature.title}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: feature.description
                      .split("Sigmund")
                      .join('<span class="sigmund-text">Sigmund</span>'),
                  }}
                ></p>
              </li>
            ))}
          </ul>
        </section>

        <section className="content-section">
          <h2>
            Why <span className="sigmund-text">Sigmund</span> is a Game-Changer:
          </h2>
          <ul>
            {[
              {
                title: "Improved Assessment Accuracy",
                description:
                  "By combining clinical expertise with advanced data analytics, Sigmund helps lower the risk of mis/overdiagnosis and misprescription by better-informing decisions.",
              },
              {
                title: "Efficient Patient Care",
                description:
                  "Sigmund's AI-powered predictions quickly inform the diagnostic process, saving time for both patients and physicians while ensuring comprehensive assessments.",
              },
              {
                title: "Better Patient Outcomes",
                description:
                  "By predictive probabilities early, Sigmund helps improve treatment efficacy, resulting in better mental health outcomes for patients.",
              },
              {
                title: "EMR integration",
                description:
                  'Sigmund can be integrated into many electronic medical record ("EMR") platforms. Integration streamlines the doctor/patient relationship.',
              },
            ].map((benefit, index) => (
              <li key={index}>
                <h3>{benefit.title}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: benefit.description
                      .split("Sigmund")
                      .join('<span class="sigmund-text">Sigmund</span>'),
                  }}
                ></p>
              </li>
            ))}
          </ul>
        </section>

        <section className="content-section">
          <p>
            <span className="sigmund-text">Sigmund</span> helps bridge the gap
            between general medicine and improved mental health care.
          </p>
          <p>
            Patient orders with a Primary Care Physician/EMR Code will be
            charged a flat rate of $800.00 per assessment (includes custom DNA
            panel/Performance Report) or $9.00 for Questionnaire-only assessment
            (anxiety and depression predispositions without DNA/Performance
            Report). Codes are issued on an annual basis. PCPs and EMRs who
            register here will receive a{" "}
            <span className="sigmund-text">Sigmund</span> CODE that can be used
            by their patients to access the pricing discount.
          </p>
        </section>

        <div className="important-note">
          <p>
            *there are approximately eight primary care physicians for each
            Psychiatrist and this ratio creates a bottleneck that results in
            long wait times and contributes to misprescription and poor
            outcomes.
          </p>
        </div>
      </div>
    </SharedLayout>
  );
};

export default PCPView;
